import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch, ArrowRight, Search, X } from 'lucide-react';
import { frameworkGraph, getFrameworkById, frameworkMetadata } from '../data/relationshipGraph';
import type { RelationshipType } from '../types/relationships';

interface ConnectionPathProps {
  fromFrameworkId?: string;
  className?: string;
}

interface PathNode {
  frameworkId: string;
  relationshipType?: RelationshipType;
  strength?: number;
}

export const ConnectionPath: React.FC<ConnectionPathProps> = ({
  fromFrameworkId,
  className = '',
}) => {
  const [fromId, setFromId] = useState(fromFrameworkId || '');
  const [toId, setToId] = useState('');
  const [path, setPath] = useState<PathNode[] | null>(null);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const handleFindPath = () => {
    if (!fromId || !toId) return;
    
    const foundPath = findShortestPath(fromId, toId);
    setPath(foundPath);
    setSearchPerformed(true);
  };

  const handleClear = () => {
    setToId('');
    setPath(null);
    setSearchPerformed(false);
  };

  return (
    <div className={`bg-[var(--bg-raised)]  shadow-lg border-2 border-[var(--mustard)] overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-coral-50 border-b-2 border-[var(--mustard)]">
        <div className="flex items-center gap-3">
          <GitBranch className="h-6 w-6 text-[var(--mustard)]" />
          <div>
            <h3 className="font-bold text-charcoal-900">Connection Pathfinder</h3>
            <p className="text-xs text-charcoal-600">
              Discover how frameworks are connected (up to 6 degrees)
            </p>
          </div>
        </div>
      </div>

      {/* Search Interface */}
      <div className="p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          {/* From Framework */}
          <div>
            <label className="block text-sm font-semibold text-charcoal-900 mb-2">
              From Framework:
            </label>
            <select
              value={fromId}
              onChange={(e) => setFromId(e.target.value)}
              className="w-full px-3 py-2 border-2 border-charcoal-200 focus:border-[var(--mustard)] focus:outline-none text-sm"
            >
              <option value="" key="empty-from">Select framework...</option>
              {frameworkMetadata.map(fw => (
                <option key={fw.encodedId} value={fw.encodedId}>
                  {fw.name}
                </option>
              ))}
            </select>
          </div>

          {/* To Framework */}
          <div>
            <label className="block text-sm font-semibold text-charcoal-900 mb-2">
              To Framework:
            </label>
            <select
              value={toId}
              onChange={(e) => setToId(e.target.value)}
              className="w-full px-3 py-2 border-2 border-charcoal-200 focus:border-[var(--mustard)] focus:outline-none text-sm"
            >
              <option value="" key="empty-to">Select framework...</option>
              {frameworkMetadata
                .filter(fw => fw.encodedId !== fromId)
                .map(fw => (
                  <option key={fw.encodedId} value={fw.encodedId}>
                    {fw.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleFindPath}
            disabled={!fromId || !toId}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--mustard)] hover:bg-[var(--mustard)] disabled:bg-charcoal-300 disabled:cursor-not-allowed text-white font-medium transition-colors"
          >
            <Search className="h-4 w-4" />
            Find Connection
          </button>
          {searchPerformed && (
            <button
              onClick={handleClear}
              className="flex items-center gap-2 px-4 py-2 bg-charcoal-200 hover:bg-charcoal-300 text-charcoal-700 font-medium transition-colors"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>

        {/* Results */}
        {searchPerformed && (
          <div className="mt-6">
            {path === null ? (
              <div className="bg-coral-50 border-2 border-coral-300 p-6 text-center">
                <p className="text-coral-800 font-semibold mb-2">No Connection Found</p>
                <p className="text-sm text-coral-700">
                  These frameworks are not connected within 6 degrees. They may be in different domains or require additional relationships to be defined.
                </p>
              </div>
            ) : path.length === 1 ? (
              <div className="bg-sage-50 border-2 border-sage-300 p-6 text-center">
                <p className="text-sage-800 font-semibold mb-2">Same Framework!</p>
                <p className="text-sm text-sage-700">
                  You selected the same framework for both start and end points.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Path Summary */}
                <div className="bg-gradient-to-r from-amber-50 to-sage-50 border-2 border-[var(--mustard)] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-charcoal-900">
                        Connection Found!
                      </p>
                      <p className="text-xs text-charcoal-600 mt-1">
                        {path.length - 1} degree{path.length === 2 ? '' : 's'} of separation
                      </p>
                    </div>
                    <div className="px-4 py-2 bg-[var(--bg-raised)] border border-[var(--mustard)]">
                      <span className="text-2xl font-bold text-[var(--mustard)]">
                        {path.length - 1}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Path Visualization */}
                <div className="space-y-3">
                  {path.map((node, idx) => {
                    const framework = getFrameworkById(node.frameworkId);
                    if (!framework) return null;

                    const isLast = idx === path.length - 1;

                    return (
                      <div key={idx}>
                        {/* Framework Node */}
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-12 text-center">
                            <div className="inline-flex items-center justify-center w-10 h-10 bg-primary-600 text-white font-bold rounded-full">
                              {idx + 1}
                            </div>
                          </div>
                          <div className="flex-1">
                            <Link
                              to={`/elevation/entry/${framework.id}`}
                              className="block bg-[var(--bg-raised)] border-2 border-primary-200 hover:border-primary-400 p-4 transition-colors"
                            >
                              <h4 className="font-bold text-charcoal-900 mb-1">
                                {framework.name}
                              </h4>
                              <p className="text-xs text-charcoal-600">
                                {framework.category}
                              </p>
                            </Link>
                          </div>
                        </div>

                        {/* Relationship Arrow */}
                        {!isLast && node.relationshipType && (
                          <div className="flex items-center gap-4 ml-6 my-2">
                            <div className="w-12"></div>
                            <div className="flex items-center gap-2 text-sm">
                              <ArrowRight className="h-4 w-4 text-[var(--mustard)]" />
                              <span className="text-charcoal-600 italic">
                                {formatRelationshipType(node.relationshipType)}
                              </span>
                              {node.strength && (
                                <span className="text-xs text-charcoal-500">
                                  ({Math.round(node.strength * 100)}% strength)
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Footer */}
      <div className="bg-[var(--mustard-dim)] px-4 py-3 border-t border-[var(--mustard)]">
        <p className="text-xs text-charcoal-600 text-center">
          <span className="font-semibold">How it works:</span> We use graph algorithms to find the shortest path between frameworks based on their defined relationships.
        </p>
      </div>
    </div>
  );
};

// BFS algorithm to find shortest path
function findShortestPath(fromId: string, toId: string): PathNode[] | null {
  if (fromId === toId) {
    return [{ frameworkId: fromId }];
  }

  const queue: Array<{ id: string; path: PathNode[] }> = [
    { id: fromId, path: [{ frameworkId: fromId }] }
  ];
  const visited = new Set<string>([fromId]);
  const maxDepth = 6;

  while (queue.length > 0) {
    const current = queue.shift()!;
    
    if (current.path.length > maxDepth) {
      continue;
    }

    const relationships = frameworkGraph[current.id] || [];

    for (const rel of relationships) {
      if (rel.targetId === toId) {
        // Found the target!
        return [
          ...current.path,
          {
            frameworkId: toId,
            relationshipType: rel.type,
            strength: rel.strength,
          }
        ];
      }

      if (!visited.has(rel.targetId)) {
        visited.add(rel.targetId);
        queue.push({
          id: rel.targetId,
          path: [
            ...current.path.slice(0, -1),
            {
              ...current.path[current.path.length - 1],
              relationshipType: rel.type,
              strength: rel.strength,
            },
            { frameworkId: rel.targetId }
          ]
        });
      }
    }
  }

  return null; // No path found
}

function formatRelationshipType(type: RelationshipType): string {
  const labels: Record<RelationshipType, string> = {
    'prerequisite': 'is prerequisite for',
    'extends': 'extends',
    'contains': 'contains',
    'complement': 'complements',
    'derived-from': 'derived from',
    'applies-to': 'applies to',
    'alternative': 'alternative to',
    'conflicts': 'conflicts with',
  };
  return labels[type] || type;
}

